import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { PROBLEM_CODES, api, errorMessage, problemCode, useApiMutation, useApiQuery } from "../../api";
import type { Company } from "../../api";
import { Button, Card, EmptyState, Field, Logo, Modal, Table, useToast } from "../../components";
import type { TableColumn } from "../../components";
import { formatDate } from "../../format";
import { AsyncBoundary, PageHeader, esnEntreprisePath } from "../../shell";
import styles from "./Esn.module.css";

/** Les entreprises clientes, et la création d'une nouvelle. */
export function EsnEntreprisesPage() {
  const navigate = useNavigate();
  const { notify } = useToast();
  const entreprises = useApiQuery((o) => api.listCompanies(o), []);
  const [ouvert, setOuvert] = useState(false);
  const [nom, setNom] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const creation = useApiMutation((n: string, e: string) =>
    api.createCompany(e.trim() === "" ? { name: n } : { name: n, admin_email: e.trim() }),
  );

  async function creer(e: FormEvent) {
    e.preventDefault();
    const res = await creation.run(nom.trim(), adminEmail);
    if (res === null) {
      return;
    }
    setOuvert(false);
    if (res.invited !== null && !res.invited.mail_sent) {
      notify({
        title: "Entreprise créée, courriel non parti",
        description: `Le courriel d'invitation de ${res.invited.user.email} n'a pas pu partir. Renvoyez-le depuis la fiche.`,
        tone: "warning",
        duration: 0,
      });
    } else {
      notify({ title: `${res.company.name} créée`, tone: "success" });
    }
    navigate(esnEntreprisePath(res.company.id));
  }

  const code = problemCode(creation.error);
  const erreurNom = code === PROBLEM_CODES.invalidCompanyName ? "Le nom doit compter entre 1 et 120 caractères." : undefined;
  const erreurEmail =
    code === PROBLEM_CODES.emailTaken
      ? "Un compte porte déjà cette adresse."
      : code === PROBLEM_CODES.invalidEmail
        ? "Cette adresse n'a pas une forme valide."
        : undefined;
  const erreurAutre = creation.error !== null && !erreurNom && !erreurEmail ? errorMessage(creation.error) : null;

  const colonnes = useMemo<TableColumn<Company>[]>(
    () => [
      {
        key: "nom",
        header: "Entreprise",
        render: (c) => (
          <span className={styles.identite}>
            <Logo src={c.logo_url} name={c.name} size="sm" />
            <span>{c.name}</span>
          </span>
        ),
      },
      {
        key: "portefeuille",
        header: "Portefeuille",
        render: (c) => <code className={styles.code}>{c.wallet_id.slice(0, 8)}…</code>,
      },
      { key: "creee", header: "Créée le", render: (c) => formatDate(c.created_at) },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Espace ESN"
        title="Entreprises clientes"
        description="Chaque entreprise a son portefeuille de tokens et ses membres. Ouvrez une fiche pour gérer son équipe et suivre ses missions."
        actions={
          <Button variant="primary" onClick={() => setOuvert(true)}>
            Nouvelle entreprise
          </Button>
        }
      />

      <AsyncBoundary
        state={entreprises}
        errorTitle="Entreprises illisibles"
        skeleton={<Table columns={colonnes} rows={[]} rowKey={(c) => c.id} caption="Entreprises" loading animateRows={false} />}
      >
        {(donnees) => (
          <Card padding="none">
            <Table
              columns={colonnes}
              rows={donnees.companies}
              rowKey={(c) => c.id}
              caption="Entreprises clientes"
              animateRows={false}
              onRowClick={(c) => navigate(esnEntreprisePath(c.id))}
              empty={
                <EmptyState
                  compact
                  title="Aucune entreprise cliente"
                  description="Créez la première : elle recevra son portefeuille, et son administrateur une invitation."
                  action={
                    <Button variant="primary" onClick={() => setOuvert(true)}>
                      Nouvelle entreprise
                    </Button>
                  }
                />
              }
            />
          </Card>
        )}
      </AsyncBoundary>

      <Modal
        open={ouvert}
        onClose={() => {
          setOuvert(false);
          creation.reset();
        }}
        title="Nouvelle entreprise"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOuvert(false)} disabled={creation.pending}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" form="creation-entreprise" loading={creation.pending} disabled={nom.trim() === ""}>
              Créer
            </Button>
          </>
        }
      >
        <form id="creation-entreprise" className={styles.formulaire} onSubmit={(e) => void creer(e)} noValidate>
          {erreurAutre !== null && (
            <p className={styles.alerte} role="alert">
              {erreurAutre}
            </p>
          )}
          <Field label="Nom de l'entreprise" error={erreurNom} required>
            {(commande) => (
              <input {...commande} type="text" maxLength={120} value={nom} onChange={(e) => setNom(e.target.value)} disabled={creation.pending} />
            )}
          </Field>
          <Field
            label="Adresse du premier administrateur"
            hint="Facultatif. La personne reçoit une invitation à créer son mot de passe."
            error={erreurEmail}
          >
            {(commande) => (
              <input
                {...commande}
                type="email"
                inputMode="email"
                spellCheck={false}
                autoComplete="off"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                disabled={creation.pending}
              />
            )}
          </Field>
        </form>
      </Modal>
    </>
  );
}
